import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { UserDetail } from '../../../models/users.model';
import { UserService } from '../../../services/APIs/Users/userservice';
import { BaseValueAccessorComponent } from '../../../common/base-component/base-value-accessor.component';
import { AbstractControl, NgControl } from '@angular/forms';
import { tap } from 'rxjs/operators';
import { DataContextService } from 'src/app/services/datacontext.service';

@Component({
  selector: 'app-owner-search',
  templateUrl: './owner-search.component.html',
  styleUrls: ['./owner-search.component.less'],
})
export class OwnerSearchComponent extends BaseValueAccessorComponent implements OnInit {
  @Input() placeholder: string = 'Translation.Search_by_name_or_email_text';
  @Input() showChips: boolean = true;
  @Input() disabled: boolean = false;
  @Input() isRequired = false;
  @Input() label: string = '';
  @Input() errorMessage = '';
  @Input() description: string = '';

  @Input()
  set selectedData(value: SelectedOwnerItem[]) {
    if (value) {
      if (!this.showChips) {
        if (value.length) {
          this.value = {
            value: value[0].id,
            label: `${value[0].firstName} ${value[0].lastName}`,
          };
        } else {
          this.value = null;
        }
      }
      this.chips = value;
    }
  }

  @Input() useAssignee: boolean = true;
  @Input() isExternal: boolean = false;
  value: {
    label?: string;
    value?: number;
  };
  subject$: Subject<string> = new Subject();
  ownersList$: BehaviorSubject<(UserDetail & { isAlreadyAdded?: boolean })[]> = new BehaviorSubject([]);
  chips: SelectedOwnerItem[] = [];
  overflowLimit: number = 40; //preference limit should be assigned here;
  noResultFound: boolean = false;
  stopLoader: boolean;

  get isError(): boolean {
    if (this.control) {
      return this.control.touched && this.control.invalid;
    }
    return false;
  }

  @Output() selected: EventEmitter<SelectedOwnerItem[]> = new EventEmitter();
  @Output() clear: EventEmitter<void> = new EventEmitter();
  @Output() searchChange: EventEmitter<string> = new EventEmitter();

  get control(): AbstractControl {
    return this.ngControl ? this.ngControl.control : null;
  }

  constructor(private userService: UserService, @Optional() private ngControl: NgControl, private cdRef: ChangeDetectorRef) {
    super();
    ngControl && (ngControl.valueAccessor = this);
  }

  markAsTouched(): void {
    if (this.ngControl) {
      this.onTouchedCallback();
      this.cdRef.detectChanges();
    }
  }

  ngOnInit(): void {
    this.searchOwner();
  }

  writeValue(value: SelectedOwnerItem[]) {
    if (value) {
      if (!this.showChips) {
        if (value.length) {
          this.value = {
            value: value[0].id,
            label: `${value[0].firstName} ${value[0].lastName}`,
          };
        } else {
          this.value = null;
        }
      }
      this.chips = value;
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  private searchOwner() {
    this.subject$
      .pipe(
        tap((_) => {
          this.onTouchedCallback();
          this.ownersList$.next([]);
          this.stopLoader = null;
        })
      )
      .subscribe((value) => {
        this.searchChange.emit(value);
        if (this.useAssignee) {
          this.userService.getAssignees(value, (DataContextService.ROWS_PER_PAGE || 40)).subscribe((res: UserDetail[]) => {
            this.stopLoader = true;
            this.noResultFound = !res.length ? true : false;
            const owners = this.mapOwners(res);
            this.ownersList$.next(owners);
          });
        } else {
          this.userService.getUserSearch(value, (DataContextService.ROWS_PER_PAGE || 40)).subscribe((res: UserDetail[]) => {
            this.stopLoader = true;
            const owners = this.mapOwners(res);
            this.noResultFound = !res.length ? true : false;
            this.ownersList$.next(owners);
          });
        }
      });
  }

  private mapOwners(res: UserDetail[]) {
    return res.map((i) => {
      return {
        ...i,
        isAlreadyAdded: this.showChips ? !!(this.chips && this.chips.find((d) => d.id === i.id)) : false,
      };
    });
  }

  select(user: UserDetail) {
    if (this.showChips) {
      this.chips = [
        ...this.chips,
        {
          firstName: user.firstName,
          lastName: user.lastName,
          id: user.id,
          email: user.email,
          title: user.title,
          shortTitle: user.shortTitle,
          userName: user.userName,
        },
      ];
    } else {
      this.chips = [
        {
          firstName: user.firstName,
          lastName: user.lastName,
          id: user.id,
          email: user.email,
          title: user.title,
          shortTitle: user.shortTitle,
          userName: user.userName,
        },
      ];
    }
    this.value = { value: null, label: `` };
    this.emptySearchList();
    if (!this.showChips) {
      this.value = { value: user.id, label: `${user.firstName} ${user.lastName}` };
    }

    this.selected.emit(this.chips);
    this.updateControl();
  }

  remove(index: number) {
    this.chips.splice(index, 1);
    this.selected.emit(this.chips);
    this.updateControl();
  }

  updateControl(): void {
    if (this.ngControl) {
      this.onTouchedCallback();
      this.onChangeCallback(this.chips);
      this.cdRef.detectChanges();
    }
  }
  private emptySearchList() {
    this.ownersList$.next([]);
    this.noResultFound = false;
  }
  clearData() {
    this.emptySearchList();
    this.onTouchedCallback();
    if (!this.showChips && this.chips.length > 0) {
      this.chips = [];
      this.onChangeCallback(this.chips);
      this.clear.emit();
    }
  }
}

export class SelectedOwnerItem {
  firstName: string;
  lastName: string;
  id: number;
  email?: string;
  title?: string;
  shortTitle?: string;
  label?: string;
  value?: string;
  userName?: string;
}
